/**
 * 약관 관련 데이터를 관리하는 컴포저블
 * 약관 전체 조회, 약관 목록 조회, 약관 상세 조회 기능을 제공합니다.
 */

import type { ResponseTermsByTermsName, ResponseTermsList } from '@template/api';
import { termsService } from '@/services/api';
import { ref, computed } from 'vue';

/**
 * 약관 전체 데이터 타입 (약관 이름별로 그룹화됨)
 */
export type TermsGroupedData = { [key: string]: ResponseTermsByTermsName[] };

export function useTerm() {
  // 로컬 상태
  const loading = ref(false);
  const error = ref<string | null>(null);

  // 약관 전체 데이터 (약관 이름별로 그룹화됨)
  const termsGrouped = ref<TermsGroupedData>({});

  // 약관 목록 데이터
  const termsList = ref<ResponseTermsList | null>(null);

  // 약관 상세 데이터 (특정 약관 이름의 버전 리스트)
  const termsDetail = ref<ResponseTermsByTermsName[]>([]);

  // 현재 선택된 약관 이름
  const selectedTermsName = ref<string | null>(null);

  /**
   * 약관 전체를 조회합니다.
   * 약관 이름별로 그룹화된 데이터를 반환합니다.
   */
  const loadAllTermsGrouped = async () => {
    try {
      loading.value = true;
      error.value = null;

      const response = await termsService.getAllTermsGroupedByTermsName();

      if (response && response.data) {
        termsGrouped.value = response.data;
      } else {
        termsGrouped.value = {};
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '약관 전체 목록을 불러오는데 실패했습니다.';
      error.value = errorMessage;
      termsGrouped.value = {};
      console.error('약관 전체 목록 로드 실패:', err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * 약관 목록을 조회합니다.
   * 필수 약관과 선택 약관 목록을 반환합니다.
   */
  const loadTermsList = async () => {
    try {
      loading.value = true;
      error.value = null;

      const response = await termsService.getAllTermsNames();

      if (response && response.data) {
        termsList.value = response.data;
      } else {
        termsList.value = null;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '약관 목록을 불러오는데 실패했습니다.';
      error.value = errorMessage;
      termsList.value = null;
      console.error('약관 목록 로드 실패:', err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * 특정 약관의 상세 정보를 조회합니다.
   * @param termsName - 조회할 약관 이름
   */
  const loadTermsDetail = async (termsName: string) => {
    try {
      loading.value = true;
      error.value = null;

      const response = await termsService.getTermsByTermsName(termsName);

      if (response && response.data && response.data.contents) {
        termsDetail.value = response.data.contents;
        selectedTermsName.value = termsName;
      } else {
        termsDetail.value = [];
        selectedTermsName.value = null;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '약관 상세 정보를 불러오는데 실패했습니다.';
      error.value = errorMessage;
      termsDetail.value = [];
      selectedTermsName.value = null;
      console.error('약관 상세 정보 로드 실패:', err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * 가장 최신 약관을 조회합니다.
   * @param termsName - 조회할 약관 이름
   * @returns 가장 최신 약관 정보 또는 null
   */
  const loadLatestTerms = async (termsName: string): Promise<ResponseTermsByTermsName | null> => {
    try {
      loading.value = true;
      error.value = null;

      const response = await termsService.getTermsByTermsName(termsName);

      if (
        response &&
        response.data &&
        response.data.contents &&
        response.data.contents.length > 0
      ) {
        // 날짜순 정렬 (최신순)
        const sortedTerms = [...response.data.contents].sort(
          (a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
        );
        return sortedTerms[0];
      }

      return null;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '최신 약관을 불러오는데 실패했습니다.';
      error.value = errorMessage;
      console.error('최신 약관 로드 실패:', err);
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * 데이터를 새로고침합니다.
   */
  const refreshTermsGrouped = async () => {
    await loadAllTermsGrouped();
  };

  /**
   * 약관 목록을 새로고침합니다.
   */
  const refreshTermsList = async () => {
    await loadTermsList();
  };

  /**
   * 약관 상세를 새로고침합니다.
   */
  const refreshTermsDetail = async () => {
    if (selectedTermsName.value) {
      await loadTermsDetail(selectedTermsName.value);
    }
  };

  /**
   * 선택된 약관을 초기화합니다.
   */
  const resetSelectedTerms = () => {
    selectedTermsName.value = null;
    termsDetail.value = [];
  };

  // 계산된 속성들
  const hasTermsGrouped = computed(() => Object.keys(termsGrouped.value).length > 0);
  const hasTermsList = computed(() => termsList.value !== null);
  const hasTermsDetail = computed(() => termsDetail.value.length > 0);
  const isTermsSelected = computed(() => selectedTermsName.value !== null);

  const requiredTerms = computed(() => {
    if (!termsList.value) return [];
    const terms = Array.isArray(termsList.value.required)
      ? termsList.value.required
      : [termsList.value.required];
    return terms.filter((t) => t !== 'OPEN_API_TERMS_OF_USE');
  });

  const optionalTerms = computed(() => {
    if (!termsList.value) return [];
    const terms = Array.isArray(termsList.value.optional)
      ? termsList.value.optional
      : [termsList.value.optional];
    return terms.filter((t) => t !== 'OPEN_API_TERMS_OF_USE');
  });

  // 약관 그룹화 데이터에서 특정 약관 가져오기
  const getTermsFromGrouped = (termsName: string): ResponseTermsByTermsName[] => {
    return termsGrouped.value[termsName] || [];
  };

  // 가장 최신 약관 정보 (termsDetail 기준)
  const latestTerms = computed<ResponseTermsByTermsName | null>(() => {
    if (termsDetail.value.length === 0) return null;

    const sortedTerms = [...termsDetail.value].sort(
      (a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
    );

    return sortedTerms[0];
  });

  return {
    // 상태
    loading,
    error,
    termsGrouped,
    termsList,
    termsDetail,
    selectedTermsName,

    // 계산된 속성
    hasTermsGrouped,
    hasTermsList,
    hasTermsDetail,
    isTermsSelected,
    requiredTerms,
    optionalTerms,
    latestTerms,

    // 메서드
    loadAllTermsGrouped,
    loadTermsList,
    loadTermsDetail,
    loadLatestTerms,
    refreshTermsGrouped,
    refreshTermsList,
    refreshTermsDetail,
    resetSelectedTerms,
    getTermsFromGrouped,
  };
}
